// ============================================
// PRTCL (formerly PenRx) Calculation Utilities
// Safety-critical - all dosing math lives here
// ============================================

import type {
  CalculatorInputs,
  CalculatorResults,
  DoseLog,
  MixRecipeComponent,
  Protocol,
  ProtocolComponent,
} from '../data/types';
import { SyringeType, SYRINGE_MAX_UNITS } from '../data/types';

/**
 * Calculate the concentration of a reconstituted peptide.
 * @param vialSizeMg - Amount of peptide in the vial (mg)
 * @param bacWaterMl - Amount of bacteriostatic water added (mL)
 * @returns Concentration in mcg per unit (on a U-100 syringe, 1 unit = 0.01 mL)
 */
export function calculateConcentration(vialSizeMg: number, bacWaterMl: number): number {
  if (vialSizeMg <= 0 || bacWaterMl <= 0) return 0;
  const totalMcg = vialSizeMg * 1000;
  // 1 mL = 100 units on a U-100 syringe
  const totalUnits = bacWaterMl * 100;
  return totalMcg / totalUnits;
}

/**
 * Calculate concentration in mcg per 0.1 mL (10 units on insulin syringe).
 */
export function calculateConcentrationPer01ml(vialSizeMg: number, bacWaterMl: number): number {
  return calculateConcentration(vialSizeMg, bacWaterMl) * 10;
}

/**
 * Calculate how many units to draw on the syringe for a desired dose.
 * @param desiredDoseMcg - Target dose in micrograms
 * @param vialSizeMg - Peptide vial size in mg
 * @param bacWaterMl - BAC water added in mL
 * @param syringeType - Type of syringe (U-100, U-50, U-30)
 * @returns Number of units to draw
 */
export function calculateUnitsToFill(
  desiredDoseMcg: number,
  vialSizeMg: number,
  bacWaterMl: number,
  syringeType: SyringeType = SyringeType.U100,
): number {
  if (desiredDoseMcg <= 0 || vialSizeMg <= 0 || bacWaterMl <= 0) return 0;

  const mcgPerUnit = calculateConcentration(vialSizeMg, bacWaterMl);
  if (mcgPerUnit <= 0) return 0;

  // For U-100 syringe: units = dose / concentration
  // For U-50 and U-30, the physical markings are different but each unit
  // represents the same volume (0.01 mL), so the math is the same.
  // The syringe type affects max capacity, not unit calculation.
  const units = desiredDoseMcg / mcgPerUnit;

  return Math.round(units * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate total doses available in a vial.
 */
export function calculateDosesPerVial(
  vialSizeMg: number,
  desiredDoseMcg: number,
): number {
  if (vialSizeMg <= 0 || desiredDoseMcg <= 0) return 0;
  const totalMcg = vialSizeMg * 1000;
  return Math.floor(totalMcg / desiredDoseMcg);
}

/**
 * Calculate days supply based on dosing frequency.
 * @param dosesPerVial - Number of doses in the vial
 * @param dosesPerDay - Number of doses per day (e.g., 1 for daily, 0.5 for EOD, etc.)
 * @returns Number of days the vial will last
 */
export function calculateDaysSupply(
  dosesPerVial: number,
  dosesPerDay: number,
): number {
  if (dosesPerVial <= 0 || dosesPerDay <= 0) return 0;
  return Math.floor(dosesPerVial / dosesPerDay);
}

/**
 * Estimate how many days a vial will last at the current consumption rate.
 *
 * Returns null when the rate can't be determined (vial not linked to a
 * protocol, or schedule has no interpretable frequency). Callers should
 * fall back to a count-based warning in that case.
 */
export function daysUntilExhausted(
  dosesRemaining: number | null,
  dosesPerWeek: number,
): number | null {
  // 2026-04-29: dosesRemaining is now nullable on InventoryItem (vials
  // added without a planned dose start with null counts until the first
  // log back-fills them). Treat null as "no data to project from" and
  // return null so callers can fall through to count-based fallbacks.
  if (dosesRemaining == null) return null;
  if (dosesPerWeek <= 0) return null;
  if (dosesRemaining <= 0) return 0;
  return (dosesRemaining / dosesPerWeek) * 7;
}

/**
 * Full calculator: takes all inputs and returns all outputs with validation.
 */
export function calculate(inputs: CalculatorInputs): CalculatorResults {
  const errors: string[] = [];

  const { vialSizeMg, bacWaterMl, desiredDoseMcg, syringeType } = inputs;

  // Validation
  if (vialSizeMg === null || vialSizeMg <= 0) {
    errors.push('Vial size must be greater than 0');
  }
  if (bacWaterMl === null || bacWaterMl <= 0) {
    errors.push('BAC water amount must be greater than 0');
  }
  if (desiredDoseMcg === null || desiredDoseMcg <= 0) {
    errors.push('Desired dose must be greater than 0');
  }

  if (errors.length > 0 || !vialSizeMg || !bacWaterMl || !desiredDoseMcg) {
    return {
      concentrationMcgPerUnit: 0,
      concentrationMcgPer01ml: 0,
      unitsToFill: 0,
      dosesPerVial: 0,
      isValid: false,
      errors,
    };
  }

  const concentrationMcgPerUnit = calculateConcentration(vialSizeMg, bacWaterMl);
  const concentrationMcgPer01ml = calculateConcentrationPer01ml(vialSizeMg, bacWaterMl);
  const unitsToFill = calculateUnitsToFill(desiredDoseMcg, vialSizeMg, bacWaterMl, syringeType);
  const dosesPerVial = calculateDosesPerVial(vialSizeMg, desiredDoseMcg);

  // Check if units exceed syringe capacity
  const maxUnits = SYRINGE_MAX_UNITS[syringeType];
  if (unitsToFill > maxUnits) {
    errors.push(`Dose requires ${unitsToFill} units but your ${syringeType} syringe only holds ${maxUnits} units. Consider adding less BAC water or using a larger syringe.`);
  }

  return {
    concentrationMcgPerUnit,
    concentrationMcgPer01ml,
    unitsToFill,
    dosesPerVial,
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Round a number to the nearest 0.5 increment.
 * 9.9 → 10, 9.4 → 9.5, 9.2 → 9, 9.75 → 10, 9.25 → 9.5
 */
function roundToHalf(value: number): number {
  return Math.round(value * 2) / 2;
}

/**
 * Format a number to a clean display string.
 * Rounds to nearest 0.5 for cleaner output.
 */
export function formatNumber(value: number, maxDecimals: number = 2): string {
  const rounded = roundToHalf(value);
  if (Number.isInteger(rounded)) return rounded.toString();
  return parseFloat(rounded.toFixed(maxDecimals)).toString();
}

/**
 * Format units for syringe display.
 * Rounds to nearest 0.5 unit for practical syringe markings.
 */
export function formatUnits(units: number): string {
  if (units <= 0) return '0';
  const rounded = roundToHalf(units);
  if (Number.isInteger(rounded)) return rounded.toString();
  return rounded.toFixed(1);
}

/**
 * Calculate per-component dose breakdown for a stack vial.
 * Since all components are in the same vial, the ratio is fixed.
 * Each component's dose = (component_mg / total_mg) × total_dose_mcg
 */
export function calculateStackBreakdown(
  components: { peptideName: string; amountMg: number }[],
  totalDoseMcg: number,
): { peptideName: string; doseMcg: number }[] {
  const totalMg = components.reduce((sum, c) => sum + c.amountMg, 0);
  if (totalMg <= 0) return components.map((c) => ({ peptideName: c.peptideName, doseMcg: 0 }));
  return components.map((c) => ({
    peptideName: c.peptideName,
    doseMcg: (c.amountMg / totalMg) * totalDoseMcg,
  }));
}

// ============================================================
// Add Protocol v3 - unified mixture model
// (spec: docs/add-protocol-v3-conversational-flow-spec.md, Part 1)
//
// deriveMixtureFields and groupComponents are the ONLY writers /
// readers of derived doseMcg, drawUnits, and totalDoses (C7/E4/E11 -
// acceptance criterion). No other file computes these.
// ============================================================

/**
 * Syringe-units display for a dose, standardized to 1 decimal (E4).
 * Replaces every inline `doseMcg / concentrationMcgPerUnit` copy.
 * Guards division by zero: returns 0 when either input is missing,
 * zero, or negative (audit bug #11).
 */
export function unitsForDose(doseMcg: number, concentrationMcgPerUnit: number): number {
  if (!(doseMcg > 0) || !(concentrationMcgPerUnit > 0)) return 0;
  return Math.round((doseMcg / concentrationMcgPerUnit) * 10) / 10;
}

export interface MixtureDerivation {
  /** Total mcg (all peptides) per syringe unit of the mixture. */
  concentrationMcgPerUnit: number;
  /** TRUE per-peptide amounts in one draw - the recipe ratio applied to the draw. */
  perComponent: Array<MixRecipeComponent & { doseMcg: number }>;
  /** Total mcg in one draw. */
  drawMcg: number;
  /** floor(water mL / draw mL). Null when inputs can't support it. */
  totalDoses: number | null;
  /** True when the draw exceeds the syringe barrel (S9 sanity check). */
  exceedsSyringe: boolean;
}

/**
 * Derive every stored mixture field from a combined vial's recipe,
 * water, and the user's draw. A dose of a combined vial IS the draw;
 * per-peptide mcg is the recipe ratio applied to it (D14).
 *
 * Worked example (spec Part 1): 2 mg CJC + 6 mg Ipa in 2 mL, draw
 * 20 units -> concentration 40 mcg/unit, draw 800 mcg, split
 * 200 / 600, 10 doses per vial.
 */
export function deriveMixtureFields(
  recipe: MixRecipeComponent[],
  bacWaterMl: number,
  drawUnits: number,
  syringeType: SyringeType = SyringeType.U100,
): MixtureDerivation {
  const totalMg = recipe.reduce((sum, c) => sum + c.amountMg, 0);
  if (totalMg <= 0 || bacWaterMl <= 0 || drawUnits <= 0) {
    return {
      concentrationMcgPerUnit: 0,
      perComponent: recipe.map((c) => ({ ...c, doseMcg: 0 })),
      drawMcg: 0,
      totalDoses: null,
      exceedsSyringe: false,
    };
  }

  // All supported syringes are U-100 barrels: 1 unit = 0.01 mL always
  // (C2 - barrel capacity differs, volume-per-unit does not).
  const concentrationMcgPerUnit = calculateConcentration(totalMg, bacWaterMl);
  const drawMcg = drawUnits * concentrationMcgPerUnit;
  const totalUnitsInVial = bacWaterMl * 100;
  return {
    concentrationMcgPerUnit,
    perComponent: recipe.map((c) => ({
      ...c,
      doseMcg: (c.amountMg / totalMg) * drawMcg,
    })),
    drawMcg,
    totalDoses: Math.floor(totalUnitsInVial / drawUnits),
    exceedsSyringe: drawUnits > SYRINGE_MAX_UNITS[syringeType],
  };
}

export interface ComponentGroup {
  /** Shared mixGroupId for combined groups; null for singles. */
  mixGroupId: string | null;
  /** True exactly when 2+ components share the mixGroupId. */
  isCombined: boolean;
  /** Single: the peptide name. Combined: names joined with " + ". */
  displayName: string;
  components: ProtocolComponent[];
}

/**
 * Group a protocol's components into administration units: components
 * sharing a mixGroupId form ONE combined vial; everything else is a
 * single. A mixGroupId held by only one component degrades to a
 * single ("combined" is derived from group size > 1, D14). Order
 * follows first appearance in protocol.components.
 */
export function groupComponents(protocol: Protocol): ComponentGroup[] {
  const groups: ComponentGroup[] = [];
  const byMixId = new Map<string, ComponentGroup>();

  for (const component of protocol.components) {
    const mixId = component.mixGroupId;
    if (mixId) {
      const existing = byMixId.get(mixId);
      if (existing) {
        existing.components.push(component);
        continue;
      }
      const group: ComponentGroup = {
        mixGroupId: mixId,
        isCombined: false,
        displayName: component.peptideName,
        components: [component],
      };
      byMixId.set(mixId, group);
      groups.push(group);
    } else {
      groups.push({
        mixGroupId: null,
        isCombined: false,
        displayName: component.peptideName,
        components: [component],
      });
    }
  }

  // Second pass: finalize combined-ness and display names now that
  // group sizes are known.
  for (const group of groups) {
    if (group.mixGroupId && group.components.length > 1) {
      group.isCombined = true;
      group.displayName = group.components.map((c) => c.peptideName).join(' + ');
    } else if (group.mixGroupId) {
      // Size-1 "group" degrades to a single.
      group.mixGroupId = null;
    }
  }

  return groups;
}

/**
 * Legacy combined detection (spec C7 render guard): pre-v3 combined
 * protocols have 2+ components sharing an inventoryId WITHOUT a
 * mixGroupId. Their per-peptide doses were copied, not derived, so
 * the dashboard must render a "check your dose plan" state for them -
 * never an authoritative draw instruction. Production count at
 * migration time (2026-07-02) was zero; this is defensive.
 */
export function hasLegacyCombinedComponents(protocol: Protocol): boolean {
  const counts = new Map<string, number>();
  for (const c of protocol.components) {
    if (!c.inventoryId || c.mixGroupId) continue;
    counts.set(c.inventoryId, (counts.get(c.inventoryId) ?? 0) + 1);
  }
  for (const n of counts.values()) {
    if (n >= 2) return true;
  }
  return false;
}

/**
 * One row per administration, keeping each administration's FIRST row
 * (input order preserved). The dedup key `administrationId ?? id`
 * (E10) lives ONLY here - a legacy or single log is its own
 * administration; historical rows are never backfilled.
 */
export function dedupeByAdministration(logs: DoseLog[]): DoseLog[] {
  const seen = new Set<string>();
  const out: DoseLog[] = [];
  for (const log of logs) {
    const key = log.administrationId ?? log.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(log);
  }
  return out;
}

/**
 * THE one counting helper for dose-log stats (E10). One physical
 * injection of an N-peptide mix writes N rows sharing one
 * administrationId. NEVER count raw rows for stats, streaks, or
 * history totals.
 */
export function countAdministrations(logs: DoseLog[]): number {
  return dedupeByAdministration(logs).length;
}

